'use client';

import { useEffect, useRef, type ReactNode } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const ANIMATION_DURATION_MS = 280;
const STAGGER_STEP_MS = 8;
const MAX_STAGGER_MS = 112;

interface TeamReviewsMotionProps {
  children: ReactNode;
}

function getReviewsInVisualOrder(list: HTMLDivElement) {
  const reviews = Array.from(
    list.querySelectorAll<HTMLElement>('[role="listitem"]')
  );

  return reviews.sort(
    (first, second) =>
      Number(window.getComputedStyle(first).order) -
      Number(window.getComputedStyle(second).order)
  );
}

export function TeamReviewsMotion({ children }: TeamReviewsMotionProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (
      !list ||
      typeof IntersectionObserver === 'undefined' ||
      typeof Element.prototype.animate !== 'function' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    if (reducedMotion.matches) return;

    const animations: Animation[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        if (reducedMotion.matches) return;

        getReviewsInVisualOrder(list).forEach((review, index) => {
          animations.push(
            review.animate([{ opacity: 0 }, { opacity: 1 }], {
              duration: ANIMATION_DURATION_MS,
              easing: 'ease-out',
              delay: Math.min(index * STAGGER_STEP_MS, MAX_STAGGER_MS),
              fill: 'both',
            })
          );
        });
      },
      { threshold: 0 }
    );

    const cancelAnimations = () => {
      animations.forEach((animation) => animation.cancel());
      animations.length = 0;
    };
    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      if (!event.matches) return;
      observer.disconnect();
      cancelAnimations();
    };

    let removeMotionPreferenceListener = () => {};
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', handleReducedMotionChange);
      removeMotionPreferenceListener = () =>
        reducedMotion.removeEventListener('change', handleReducedMotionChange);
    } else {
      reducedMotion.addListener(handleReducedMotionChange);
      removeMotionPreferenceListener = () =>
        reducedMotion.removeListener(handleReducedMotionChange);
    }

    observer.observe(list);

    return () => {
      observer.disconnect();
      removeMotionPreferenceListener();
      cancelAnimations();
    };
  }, []);

  return (
    <div
      ref={listRef}
      role="list"
      aria-label="Customer reviews"
      tabIndex={0}
      className="flex max-h-full scroll-pb-[120px] flex-col gap-3 overflow-y-auto pb-[120px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 md:flex-row md:items-start"
    >
      {children}
    </div>
  );
}
